import React from 'react';
import { Plus, X } from 'lucide-react';
import { FIELD_TYPES, VALIDATABLE_TEXT_TYPES, VALIDATABLE_NUMBER_TYPES } from './fieldTypes';
import ConditionEditor from './ConditionEditor';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";
const sectionClass = "pt-4 border-t border-[#F0F1F3] space-y-3";

const HAS_OPTIONS = new Set(['select', 'radio', 'checkboxGroup']);
const NO_DEFAULT_VALUE = new Set(['heading', 'paragraph', 'checkbox', 'checkboxGroup', 'rating', 'file']);
const NO_WIDTH = new Set(['heading', 'paragraph']);

/**
 * Right-hand config panel for whichever component is currently selected on
 * the canvas. Reads its field set from the same FIELD_TYPES registry the
 * renderer uses, so a new field type never needs a bespoke properties form
 * unless it genuinely needs one (options, in this phase).
 */
export default function PropertiesPanel({ component, allFields = [], onChange, onClose }) {
  if (!component) {
    return (
      <div className="p-6 text-center text-[12px] text-[#706E6B]">
        Select a field on the canvas to edit its properties.
      </div>
    );
  }

  const meta = FIELD_TYPES[component.type];
  const set = (key, value) => onChange({ ...component, [key]: value });

  const updateOption = (index, key, value) => {
    const options = [...(component.options || [])];
    options[index] = { ...options[index], [key]: value };
    set('options', options);
  };
  const addOption = () => {
    const options = [...(component.options || [])];
    options.push({ label: `Option ${options.length + 1}`, value: `option_${options.length + 1}` });
    set('options', options);
  };
  const removeOption = (index) => {
    set('options', (component.options || []).filter((_, i) => i !== index));
  };

  return (
    <div className="p-5 space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold text-[#0176D3]">{meta?.label || component.type}</p>
        <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={16} /></button>
      </div>

      {component.type === 'hidden' && (
        <>
          <div>
            <label className={labelClass}>Field Name</label>
            <input className={inputClass} value={component.label || ''} onChange={(e) => set('label', e.target.value)} placeholder="e.g. utm_campaign" />
          </div>
          <div>
            <label className={labelClass}>Default Value</label>
            <input className={inputClass} value={component.defaultValue || ''} onChange={(e) => set('defaultValue', e.target.value)} />
          </div>
          <p className="text-[11px] text-[#706E6B]">Hidden fields aren't shown to the visitor but their value is still submitted — useful for a fixed source tag or campaign id.</p>
        </>
      )}

      {component.type !== 'heading' && component.type !== 'paragraph' && component.type !== 'hidden' && (
        <>
          <div>
            <label className={labelClass}>Label</label>
            <input className={inputClass} value={component.label || ''} onChange={(e) => set('label', e.target.value)} />
          </div>

          {component.type === 'checkbox' ? (
            <div>
              <label className={labelClass}>Checkbox Text</label>
              <input className={inputClass} value={component.checkboxLabel || ''} onChange={(e) => set('checkboxLabel', e.target.value)} placeholder="Yes" />
            </div>
          ) : component.type !== 'rating' && (
            <div>
              <label className={labelClass}>Placeholder</label>
              <input className={inputClass} value={component.placeholder || ''} onChange={(e) => set('placeholder', e.target.value)} />
            </div>
          )}

          <div>
            <label className={labelClass}>Help Text</label>
            <input className={inputClass} value={component.helpText || ''} onChange={(e) => set('helpText', e.target.value)} placeholder="Optional guidance shown under the field" />
          </div>

          <label className="flex items-center gap-2 text-[13px] font-medium text-[#181818] cursor-pointer">
            <input
              type="checkbox"
              checked={!!component.required}
              onChange={(e) => set('required', e.target.checked)}
              className="accent-[#0176D3] w-4 h-4"
            />
            Required field
          </label>

          {HAS_OPTIONS.has(component.type) && (
            <div>
              <label className={labelClass}>Options</label>
              <div className="space-y-2">
                {(component.options || []).map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      className={inputClass}
                      value={opt.label}
                      onChange={(e) => updateOption(i, 'label', e.target.value)}
                      placeholder="Label"
                    />
                    <button onClick={() => removeOption(i)} className="p-2 text-[#706E6B] hover:text-red-500 shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={addOption}
                className="mt-2 flex items-center gap-1.5 text-[12px] font-bold text-[#0176D3] hover:text-[#015BA0]"
              >
                <Plus size={14} /> Add Option
              </button>
            </div>
          )}

          {component.type === 'rating' && (
            <div>
              <label className={labelClass}>Max Stars</label>
              <input
                type="number"
                min={2}
                max={10}
                className={inputClass}
                value={component.maxStars || 5}
                onChange={(e) => set('maxStars', Number(e.target.value))}
              />
            </div>
          )}

          {component.type === 'file' && (
            <div className={sectionClass}>
              <p className="text-[11px] font-bold text-[#706E6B]">Upload Settings</p>
              <div>
                <label className={labelClass}>Accepted File Types</label>
                <input
                  className={inputClass}
                  value={component.accept || ''}
                  onChange={(e) => set('accept', e.target.value)}
                  placeholder=".pdf,.jpg,.png (blank = any file)"
                />
              </div>
              <div>
                <label className={labelClass}>Max File Size (MB)</label>
                <input
                  type="number"
                  min={1}
                  max={25}
                  className={inputClass}
                  value={component.maxSizeMB ?? ''}
                  onChange={(e) => set('maxSizeMB', e.target.value === '' ? undefined : Number(e.target.value))}
                  placeholder="25"
                />
              </div>
            </div>
          )}

          {!NO_DEFAULT_VALUE.has(component.type) && (
            <div>
              <label className={labelClass}>Default Value</label>
              <input className={inputClass} value={component.defaultValue || ''} onChange={(e) => set('defaultValue', e.target.value)} placeholder="Pre-filled when the form loads" />
            </div>
          )}

          {!NO_WIDTH.has(component.type) && (
            <div>
              <label className={labelClass}>Width</label>
              <div className="flex gap-2">
                {[['full', 'Full'], ['half', 'Half']].map(([val, lbl]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => set('width', val)}
                    className={`flex-1 px-3 py-2 rounded-[4px] text-[12px] font-bold border transition-colors  whitespace-nowrap ${
                      (component.width || 'full') === val
                        ? 'border-[#0176D3] bg-[#0176D3]/5 text-[#0176D3]'
                        : 'border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3]/40'
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {VALIDATABLE_TEXT_TYPES.has(component.type) && (
            <div className={sectionClass}>
              <p className="text-[11px] font-bold text-[#706E6B]">Validation</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Min Length</label>
                  <input type="number" min={0} className={inputClass} value={component.minLength ?? ''} onChange={(e) => set('minLength', e.target.value === '' ? undefined : Number(e.target.value))} />
                </div>
                <div>
                  <label className={labelClass}>Max Length</label>
                  <input type="number" min={0} className={inputClass} value={component.maxLength ?? ''} onChange={(e) => set('maxLength', e.target.value === '' ? undefined : Number(e.target.value))} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Pattern (Regex)</label>
                <input className={inputClass} value={component.pattern || ''} onChange={(e) => set('pattern', e.target.value)} placeholder="e.g. ^[A-Z]{2}\\d{4}$" />
              </div>
              {component.pattern && (
                <div>
                  <label className={labelClass}>Pattern Error Message</label>
                  <input className={inputClass} value={component.patternMessage || ''} onChange={(e) => set('patternMessage', e.target.value)} placeholder="Invalid format" />
                </div>
              )}
            </div>
          )}

          {VALIDATABLE_NUMBER_TYPES.has(component.type) && (
            <div className={sectionClass}>
              <p className="text-[11px] font-bold text-[#706E6B]">Validation</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Min Value</label>
                  <input type="number" className={inputClass} value={component.min ?? ''} onChange={(e) => set('min', e.target.value === '' ? undefined : Number(e.target.value))} />
                </div>
                <div>
                  <label className={labelClass}>Max Value</label>
                  <input type="number" className={inputClass} value={component.max ?? ''} onChange={(e) => set('max', e.target.value === '' ? undefined : Number(e.target.value))} />
                </div>
              </div>
            </div>
          )}

          <ConditionEditor
            condition={component.condition}
            fields={allFields.filter((f) => f.id !== component.id)}
            onChange={(condition) => set('condition', condition)}
            title="Conditional Logic"
            emptyLabel="this field"
          />
        </>
      )}

      {component.type === 'heading' && (
        <div>
          <label className={labelClass}>Heading Text</label>
          <input className={inputClass} value={component.label || ''} onChange={(e) => set('label', e.target.value)} />
        </div>
      )}
      {component.type === 'paragraph' && (
        <div>
          <label className={labelClass}>Paragraph Text</label>
          <textarea className={`${inputClass} min-h-[80px]`} value={component.text || ''} onChange={(e) => set('text', e.target.value)} />
        </div>
      )}
    </div>
  );
}
